import React from 'react';
import PropTypes from 'prop-types';
import { tr } from '@/common/utils/tr';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

SnackbarMessage.propTypes = {
    i18nKey: PropTypes.string.isRequired,
    replacements: PropTypes.object
};

SnackbarText.propTypes = {
    sourceText: PropTypes.string.isRequired
};

/**
 * Snackbar message text rendered inside React so it picks up the translation later.
 *
 * Calling trI18n() at the call site produces a plain string, and notistack keeps that
 * value as is. When the dictionary does not have the string yet, tr() returns the source
 * text and only queues a MISS, so the toast would stay untranslated forever.
 * Rendering the text here lets useTranslationSubscription() re-render it once the MISS
 * is flushed and the translation arrives.
 *
 * Pass it as the notistack message:
 *   enqueueSnackbar(<SnackbarMessage i18nKey="MSG-CM-UPDATE-DATA-SUCCESS" />, { ... })
 *
 * A toast shorter than the dictionary flush interval (3s) can hide before the translation
 * arrives, but the next toast for the same key then shows the translated text.
 *
 * `replacements` fills placeholders left in the message, e.g. { '{{langs}}': 'EN, JP' }.
 * It runs after the translation so the placeholder survives the trip through the engine.
 */
export default function SnackbarMessage({ i18nKey, replacements }) {
    useTranslationSubscription();

    let text = trI18n(i18nKey);

    if (replacements && typeof text === 'string') {
        Object.keys(replacements).forEach(placeholder => {
            text = text.replace(placeholder, replacements[placeholder]);
        });
    }

    return <>{text}</>;
}

/**
 * Same idea as SnackbarMessage, for a hardcoded literal that goes through tr()
 * instead of a locale key.
 */
export function SnackbarText({ sourceText }) {
    useTranslationSubscription();

    return <>{tr(sourceText)}</>;
}

/**
 * One cached element per key, so the same key always yields the same object.
 *
 * SnackbarProvider is configured with preventDuplicate, and notistack compares queued
 * messages with `item.message === message`. A fresh element on every call would never
 * match and duplicate toasts would slip through, so the reference has to stay stable.
 * Elements are immutable descriptors, so sharing one across several toasts is safe.
 */
const messageElementCache = new Map();

// Ceiling so replacements carrying changing values (file names, sizes) cannot grow the
// cache without bound. Dropping the oldest entry only costs the dedupe check for it.
const MAX_CACHED_MESSAGES = 200;

/**
 * Builds the message element for handler files, which hold plain logic and no JSX.
 *
 *   enqueueSnackbar(buildSnackbarMessage('MSG-CM-UPDATE-DATA-SUCCESS'), { ... })
 *   enqueueSnackbar(buildSnackbarMessage('MSG-X', { '{{langs}}': names }), { ... })
 */
export function buildSnackbarMessage(i18nKey, replacements) {
    // Not a usable key: hand the value back so the caller keeps its current behaviour.
    if (typeof i18nKey !== 'string' || i18nKey.length === 0) {
        return i18nKey;
    }

    const cacheKey = replacements ? `${i18nKey}${JSON.stringify(replacements)}` : i18nKey;

    if (!messageElementCache.has(cacheKey)) {
        if (messageElementCache.size >= MAX_CACHED_MESSAGES) {
            messageElementCache.delete(messageElementCache.keys().next().value);
        }
        messageElementCache.set(cacheKey, <SnackbarMessage i18nKey={i18nKey} replacements={replacements} />);
    }

    return messageElementCache.get(cacheKey);
}

const textElementCache = new Map();

/**
 * buildSnackbarMessage() for a hardcoded literal instead of a locale key.
 *
 *   enqueueSnackbar(buildSnackbarText('保留時間が上限を超えました。'), { ... })
 */
export function buildSnackbarText(sourceText) {
    if (typeof sourceText !== 'string' || sourceText.length === 0) {
        return sourceText;
    }

    if (!textElementCache.has(sourceText)) {
        if (textElementCache.size >= MAX_CACHED_MESSAGES) {
            textElementCache.delete(textElementCache.keys().next().value);
        }
        textElementCache.set(sourceText, <SnackbarText sourceText={sourceText} />);
    }

    return textElementCache.get(sourceText);
}
